import type { ShipmentBox } from 'common/src/utils/types';

export type ShipmentDistributionEntry = { label: string; value: number };

export type ShipmentDimensionFilter = {
  id: string;
  title: string;
  getValue: (box: ShipmentBox) => string | null | undefined;
};

export const shipmentDimensionKey = (value: string | null | undefined) =>
  value?.trim() ? `value:${value.trim()}` : 'missing';

export function filterShipmentDimensions<T extends ShipmentBox>(
  boxes: T[],
  dimensions: ShipmentDimensionFilter[],
  selected: Record<string, string>
): T[] {
  const active = dimensions.filter((dimension) => selected[dimension.id]);
  return boxes.filter((box) =>
    active.every(
      (dimension) =>
        shipmentDimensionKey(dimension.getValue(box)) === selected[dimension.id]
    )
  );
}

export function compactShipmentDistribution(
  entries: ShipmentDistributionEntry[],
  limit: number,
  otherLabel: string
): (ShipmentDistributionEntry & { members?: ShipmentDistributionEntry[] })[] {
  if (entries.length <= limit) return entries;
  const remaining = entries.slice(limit);
  return [
    ...entries.slice(0, limit),
    {
      label: otherLabel,
      value: remaining.reduce((sum, entry) => sum + entry.value, 0),
      members: remaining,
    },
  ];
}

export function getShipmentDistribution(
  boxes: ShipmentBox[],
  getLabel: (box: ShipmentBox) => string | null | undefined,
  metric: 'kg' | 'ceq',
  equivalentBoxWeight: number,
  missingLabel: string
) {
  const totals = new Map<string, number>();
  for (const box of boxes) {
    const label = getLabel(box)?.trim() || missingLabel;
    const kilos = (box.quantity || 0) * (box.box_weight || 0);
    const value = metric === 'kg' ? kilos : kilos / equivalentBoxWeight;
    totals.set(label, (totals.get(label) ?? 0) + value);
  }
  return Array.from(totals, ([label, value]) => ({ label, value })).sort(
    (a, b) => b.value - a.value || a.label.localeCompare(b.label)
  );
}
